import apiClient, { artworkCmd } from './apiClient';

/**
 * What My Stuff reads of Flobi Drive: the person's media and the workspace's, through the
 * gateway, as the signed-in person — the same calls Drive makes for itself. (The file's
 * brand is read through artwork-back: `services/work.ts`.)
 *
 * The rows are read loosely and everything that does not fit is left out: a thing with no
 * address cannot be placed, and a tile that cannot be placed is worse than no tile.
 */

/** The kinds My Stuff keeps apart. An SVG is Drive's (media's) VECTOR. */
export type MediaKind = 'image' | 'video' | 'svg';

/** A picture, clip or SVG in the workspace's Drive, as My Stuff shows and places it. */
export interface DriveMedia {
  /** The media asset's id: what a document's layer points at. */
  readonly id: string;
  readonly assetId: string;
  readonly name: string;
  readonly kind: MediaKind;
  /** A small rendition, for the tile: a clip's poster. */
  readonly thumb: string;
  /** The thing itself: what the document keeps and the canvas draws. */
  readonly src: string;
  readonly width: number;
  readonly height: number;
  readonly mime?: string;
  /** Its size on disk, when Drive says. */
  readonly bytes?: number;
}

/**
 * How big a clip may be to go on a page (Tarek, 2026-10-01: "we need to cap video sizes and
 * length"). The numbers are the proposal he was shown; the length is checked once the clip's
 * duration is known — Drive's list does not carry it.
 */
export const VIDEO_CAP = { seconds: 60, megabytes: 50 } as const;

/**
 * Why a clip may not go on a page, or null when it may: bigger than the cap, or — once its
 * length is known — longer.
 */
export function overCap(media: Pick<DriveMedia, 'kind' | 'bytes'> & { duration?: number }): string | null {
  if (media.kind !== 'video') return null;
  if (media.bytes && media.bytes > VIDEO_CAP.megabytes * 1_000_000) return `Over ${VIDEO_CAP.megabytes} MB`;
  if (media.duration && media.duration > VIDEO_CAP.seconds) return `Over ${VIDEO_CAP.seconds} seconds`;
  return null;
}

/** Why a clip was not placed, said the one way everywhere: what it is over, and what the cap is. */
export function capSentence(name: string, over: string): string {
  // Only its first letter is lowered: "Over 50 MB" said in a sentence is "over 50 MB", never "50 mb".
  return `${name} is ${over.charAt(0).toLowerCase()}${over.slice(1)}: clips up to ${VIDEO_CAP.seconds} seconds and ${VIDEO_CAP.megabytes} MB go on a page.`;
}

/** What a seat that does not include the other app is told, in place of a list. */
class NotIncluded extends Error {}

async function command<T>(path: string, body: Record<string, unknown>, app: string, signal?: AbortSignal): Promise<T> {
  try {
    const response = await apiClient.post(path, body, signal ? { signal } : undefined);
    const data = response?.data;
    if (data && data.success === false) throw new Error(typeof data.error === 'string' ? data.error : (data.error?.message ?? `${app} could not be read.`));
    return (data && typeof data === 'object' && 'data' in data && !Array.isArray(data) ? data : { data }) as T;
  } catch (error) {
    if (error instanceof NotIncluded || signal?.aborted) throw error;
    const status = (error as { response?: { status?: number } }).response?.status;
    // The gateway refuses another app's routes to a seat that does not include that app.
    if (status === 403) throw new NotIncluded(`Your seat does not include ${app}.`);
    if (error instanceof Error && !('response' in error)) throw error;
    throw new Error(`${app} could not be reached.`);
  }
}

/** The pictures the canvas can draw. A vector other than SVG, or an AVIF, is not one of them. */
const DRAWABLE = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const CLIPS = new Set(['video/mp4', 'video/webm', 'video/quicktime']);

/** Which of My Stuff's kinds a file of this type is, or null for one it does not show. */
export function kindOf(mime: string | null | undefined): MediaKind | null {
  const type = String(mime ?? '').toLowerCase();
  if (DRAWABLE.has(type)) return 'image';
  if (type === 'image/svg+xml') return 'svg';
  if (CLIPS.has(type)) return 'video';
  return null;
}

interface RecentRow {
  mediaAssetId?: string;
  name?: string;
  mimeType?: string | null;
  width?: number | null;
  height?: number | null;
  sizeBytes?: number | null;
  url?: string | null;
  preview?: { thumb?: string | null; medium?: string | null };
  previewUrl?: string | null;
}

/**
 * Where in My Stuff: everything of yours newest first, what you uploaded, what you generated,
 * everybody's in the workspace, or one project's own Drive folder.
 */
export type StuffPlace = 'recent' | 'uploads' | 'generated' | 'workspace' | { readonly project: string };

/** Which kinds to show: all three, or one. */
export type KindChoice = 'all' | MediaKind;

const DRIVE_TYPE: Record<KindChoice, string | string[]> = { all: ['image', 'video', 'vector'], image: 'image', video: 'video', svg: 'vector' };

/**
 * A page of one place's media, newest first, narrowed by name when `search` is given.
 * `next` is handed back to read the page after.
 *
 * Drive answers the kind and whether it was uploaded or generated itself
 * (`drive.recent.list`'s `type` and `made`), so a page is never a page of something else
 * filtered down to nothing. A project is its Drive folder and subfolders, with what is placed
 * in its designs first (`projectFolderMedia`, `projectDesignMedia`). The folder list knows
 * types by their MIME prefix, so an SVG arrives among the images and is sorted here.
 */
export async function listMedia({ place, kind = 'all', search, cursor }: { place: StuffPlace; kind?: KindChoice; search?: string; cursor?: string | null }): Promise<{ items: DriveMedia[]; next: string | null }> {
  const wanted = (one: DriveMedia | null): one is DriveMedia => one !== null && (kind === 'all' || one.kind === kind);
  if (typeof place === 'object') {
    // The project's Drive folder, and — on the first page — what is placed in its designs: a
    // picture made in a file is filed under no project, so a project of designs read empty.
    const [drive, designs] = await Promise.allSettled([
      projectFolderMedia(place.project, kind, search, cursor),
      cursor ? Promise.resolve([]) : projectDesignMedia(place.project, search),
    ]);
    const placed = designs.status === 'fulfilled' ? designs.value.filter(wanted) : [];
    if (drive.status === 'rejected') {
      // Drive's refusal is the answer unless the designs had something to show.
      if (placed.length === 0) throw drive.reason;
      return { items: placed, next: null };
    }
    const seen = new Set(placed.map((one) => one.id));
    const items = [...placed, ...drive.value.items.filter(wanted).filter((one) => !seen.has(one.id))];
    return { items, next: drive.value.next };
  }
  const answer = await command<{ data?: { items?: RecentRow[] }; pagination?: { hasMore?: boolean; nextCursor?: string | null } }>(
    '/drive/drive.recent.list',
    {
      type: DRIVE_TYPE[kind],
      ...(place === 'workspace' ? {} : { owner: 'mine' }),
      ...(place === 'uploads' ? { made: 'uploaded' } : place === 'generated' ? { made: 'generated' } : {}),
      ...(search ? { search } : {}),
      ...(cursor ? { cursor } : {}),
      limit: PAGE,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
    },
    'Flobi Drive',
  );
  const items = (answer.data?.items ?? []).map(media).filter(wanted);
  return { items, next: answer.pagination?.hasMore ? (answer.pagination.nextCursor ?? null) : null };
}

const PAGE = 40;

/**
 * A project's own Drive folder, asked in two steps, the way Flow and Projects ask: which folder
 * backs the project (Projects), then what is in it (Drive), newest first — its subfolders too
 * (`recursive`), since a project's folder holds little but subfolders.
 */
async function projectFolderMedia(project: string, kind: KindChoice, search: string | undefined, cursor: string | null | undefined): Promise<{ items: (DriveMedia | null)[]; next: string | null }> {
  const folder = await command<{ data?: { driveFolderId?: string | null }; driveFolderId?: string | null }>('/projects/project.driveFolder.get', { projectId: project }, 'Flobi Projects');
  const folderId = folder.data?.driveFolderId ?? folder.driveFolderId ?? null;
  if (!folderId) return { items: [], next: null };
  const offset = Number(cursor) || 0;
  const answer = await command<{ data?: { files?: FolderRow[]; pagination?: { hasNext?: boolean } } }>(
    '/drive/drive.browser.getFolderMediaFiles',
    { folderId, recursive: true, sort: 'newest', type: kind === 'all' ? 'all' : kind === 'video' ? 'video' : 'image', limit: PAGE, offset, ...(search ? { search } : {}) },
    'Flobi Drive',
  );
  const items = (answer.data?.files ?? []).map((row) => media({ mediaAssetId: row.mediaAssetId ?? undefined, name: row.name, mimeType: row.mimeType, sizeBytes: row.sizeBytes, url: row.originalUrl, preview: row.previews }));
  return { items, next: answer.data?.pagination?.hasNext ? String(offset + PAGE) : null };
}

/** A picture or clip placed in one of a project's designs, as Artwork lists them (`artwork.doc.projectMedia`). */
interface DesignRow {
  mediaAssetId?: string;
  name?: string;
  mime?: string | null;
  src?: string | null;
  thumb?: string | null;
  width?: number | null;
  height?: number | null;
  bytes?: number | null;
}

/** What is placed in the project's designs the caller can see, each once, newest design first; narrowed by name. */
async function projectDesignMedia(project: string, search: string | undefined): Promise<DriveMedia[]> {
  const rows = await artworkCmd<DesignRow[] | { items?: DesignRow[] }>('artwork.doc.projectMedia', { projectId: project });
  const list = Array.isArray(rows) ? rows : (rows?.items ?? []);
  const words = search?.trim().toLowerCase();
  return list
    .map((row) => media({ mediaAssetId: row.mediaAssetId, name: row.name, mimeType: row.mime, width: row.width, height: row.height, sizeBytes: row.bytes, url: row.src, previewUrl: row.thumb }))
    .filter((one): one is DriveMedia => one !== null && (!words || one.name.toLowerCase().includes(words)));
}

/** A file in a Drive folder, as Drive lists a folder's media. */
interface FolderRow {
  mediaAssetId?: string | null;
  name?: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  originalUrl?: string | null;
  previews?: { thumb?: string | null; medium?: string | null };
}

/** A row as something My Stuff can show, or null: no address, no id, or a kind it does not show. */
function media(row: RecentRow): DriveMedia | null {
  // Drive's previewUrl is its LARGE rendition: the last resort for a tile, after thumb and medium.
  const thumb = row.preview?.thumb ?? row.preview?.medium ?? row.previewUrl;
  const kind = kindOf(row.mimeType);
  if (!row.mediaAssetId || !row.url || !thumb || !kind) return null;
  return {
    id: row.mediaAssetId,
    assetId: row.mediaAssetId,
    name: row.name || (kind === 'video' ? 'Clip' : 'Picture'),
    kind,
    thumb,
    src: row.url,
    width: Number(row.width) || 0,
    height: Number(row.height) || 0,
    ...(row.mimeType ? { mime: row.mimeType } : {}),
    ...(Number(row.sizeBytes) > 0 ? { bytes: Number(row.sizeBytes) } : {}),
  };
}

/** Generation can also use audio. Keep Drive's pagination envelope, as My Stuff does. */
export async function readReferenceMedia(kind: 'image' | 'video' | 'audio', { search, cursor, signal }: { search?: string; cursor?: string; signal?: AbortSignal } = {}) {
  const answer = await command<{ data?: { items?: (RecentRow & { durationSeconds?: number; duration?: number })[] }; pagination?: { hasMore?: boolean; nextCursor?: string | null } }>(
    '/drive/drive.recent.list', { type: kind, owner: 'mine', limit: 40, ...(search?.trim() ? { search: search.trim() } : {}), ...(cursor ? { cursor } : {}), tzOffsetMinutes: new Date().getTimezoneOffset() }, 'Flobi Drive', signal,
  );
  if (!Array.isArray(answer.data?.items)) throw Error('My Stuff did not return a media list. Try again.');
  return { rows: answer.data.items, cursor: answer.pagination?.hasMore ? answer.pagination.nextCursor || null : null };
}
